import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { OfficeLocale, OfficeTranslationKey, translateOfficeText } from "./officeTranslations";

export const OFFICE_LANGUAGE_STORAGE_KEY = "OFFICE_LANGUAGE";

export const normalizeOfficeLocale = (value?: string | null): OfficeLocale =>
    value?.trim().toLowerCase().split(/[-_]/)[0] === "nl" ? "nl" : "en";

const getStoredOfficeLocale = (): OfficeLocale => {
    if (typeof window === "undefined") return "en";
    return normalizeOfficeLocale(window.localStorage.getItem(OFFICE_LANGUAGE_STORAGE_KEY));
};

type TranslationParams = Record<string, string | number>;

interface OfficeTranslationValue {
    hasProvider: boolean;
    locale: OfficeLocale;
    intlLocale: "en-GB" | "nl-NL";
    setLocale: (locale: OfficeLocale) => void;
    t: (key: OfficeTranslationKey, params?: TranslationParams) => string;
}

const defaultValue: OfficeTranslationValue = {
    hasProvider: false,
    locale: "en",
    intlLocale: "en-GB",
    setLocale: () => undefined,
    t: (key, params) => translateOfficeText("en", key, params),
};

const OfficeTranslationContext = createContext<OfficeTranslationValue>(defaultValue);

export const OfficeTranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [locale, setLocaleState] = useState<OfficeLocale>(getStoredOfficeLocale);

    const setLocale = useCallback((nextLocale: OfficeLocale) => {
        const normalizedLocale = normalizeOfficeLocale(nextLocale);
        window.localStorage.setItem(OFFICE_LANGUAGE_STORAGE_KEY, normalizedLocale);
        setLocaleState(normalizedLocale);
    }, []);

    const value = useMemo<OfficeTranslationValue>(() => ({
        hasProvider: true,
        locale,
        intlLocale: locale === "nl" ? "nl-NL" : "en-GB",
        setLocale,
        t: (key, params) => translateOfficeText(locale, key, params),
    }), [locale, setLocale]);

    useEffect(() => {
        const previousLanguage = document.documentElement.lang;
        document.documentElement.lang = locale;
        return () => {
            document.documentElement.lang = previousLanguage;
        };
    }, [locale]);

    return (
        <OfficeTranslationContext.Provider value={value}>
            {children}
        </OfficeTranslationContext.Provider>
    );
};

export const useOfficeTranslation = () => useContext(OfficeTranslationContext);
